// ABOUTME: The dark filter sidebar for the relation Inkoopadvies page — supplier, advice basis, analysis period,
// ABOUTME: forecast horizon and the compensate-stockouts toggle. Changing any input re-runs the forecast.
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import EMGeneralExplainerBox from "../../../components/global/ModalsPopover/explainers/EMGeneralExplainerBox";
import ShPillFilter from "../../../components/theme/buttons/ShPillFilter";
import ShSwitch from "../../../components/theme/inputs/ShSwitch";
import { InputDropdown } from "../../../components/theme/dropdowns/InputDropdown";
import { useGetSuppliersSelectQuery } from "../../../newapi/global/supplier/supplierSlice";
import { tokens } from "../../../theme";
import Text from "../../../components/theme/text/Text";

const MONTHS_OPTIONS = [1, 2, 3, 6, 12];
const DAYS_OPTIONS = [14, 30, 60, 90];

const PurchaseAdviceFilters = ({ params, setParams }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    // The sidebar is a fixed dark panel in both themes, so its colours are the design-spec literals
    // below rather than theme-derived tokens.

    const { data: suppliers } = useGetSuppliersSelectQuery();

    const setParam = (key, value) => setParams((prev) => ({ ...prev, [key]: value }));
    // Supplier deselect returns null/-1 — normalise to -1 ("all"), which the slice drops.
    const setSupplier = (value) => setParam("supplierId", parseInt(value) > 0 ? parseInt(value) : -1);


    // One period selector: the shared pill bar, each option labelled with its unit.
    const PeriodTabs = ({ options, value, onSelect, unit }) => (
        <ShPillFilter
            options={options.map((opt) => ({ value: opt, label: `${opt} ${t(unit)}` }))}
            value={value}
            onChange={onSelect}
        />
    );

    return (
        <Box
            sx={{
                flex:1,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 3,
                p: 3,
                borderRadius: 3,
                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.txt['primary'],
            }}
        >
            {/* Supplier — restrict the forecast to one supplier's products. */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Text color={theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light']}>{t("supplier")}</Text>
                <InputDropdown
                    variant="darker"
                    displayName="name"
                    valueKey="id"
                    closeOnSelection
                    selected={params.supplierId > 0 ? params.supplierId : -1}
                    options={suppliers ?? []}
                    noSelection={t("all_suppliers")}
                    onChange={setSupplier}
                    searchOptions={{ enabled: true, placeholder: t("search_supplier_placeholder") }}
                    height={32}
                    idSX={{
                        background: "rgba(255,255,255,.09)",
                        border: "1px solid rgba(255,255,255,.16)",
                        "&:hover": { background: "rgba(255,255,255,.15)" },
                        "& .MuiTypography-root": { color: "#fff" },
                    }}
                />
            </Box>

            {/* Advice basis — predicted sales, or the current stock level against each product's
                purchase strategy (backorders / safety stock). */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, "& svg": { color: theme.palette.mode === "dark" ? colors.txt['secondary'] : colors.txt['softened'] } }}>
                    <Text color={theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light']}>{t("advice_basis")}</Text>
                    <EMGeneralExplainerBox
                        title={t("advice_basis")}
                        text={t("advice_basis_tooltip")}
                    />
                </Box>
                <ShPillFilter
                    options={[
                        { value: "forecast", label: t("advice_basis_forecast") },
                        { value: "stocklevel", label: t("advice_basis_stock_level") },
                    ]}
                    value={params.basis}
                    onChange={(basis) => setParam("basis", basis)}
                />
            </Box>

            {/* The inputs below only steer the sales forecast, so they make way in stock-level mode. */}
            {params.basis !== "stocklevel" && (
            <>
            {/* Analysis period — how many months of sales velocity to measure over. The window is
                the current month so far plus this many full months before it, which the explainer
                spells out because "1 month" on its own reads as the last 30 days. */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, "& svg": { color: theme.palette.mode === "dark" ? colors.txt['secondary'] : colors.txt['softened'] } }}>
                    <Text color={theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light']}>{t("analysis_period")}</Text>
                    <EMGeneralExplainerBox
                        title={t("analysis_period")}
                        text={t("analysis_period_tooltip")}
                    />
                </Box>
                <PeriodTabs
                    options={MONTHS_OPTIONS}
                    value={params.monthsBack}
                    onSelect={(m) => setParam("monthsBack", m)}
                    unit="months_short"
                />
            </Box>

            {/* Forecast horizon — how many days ahead to predict consumption for. */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Text color={theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light']}>{t("forecast_horizon")}</Text>
                <PeriodTabs
                    options={DAYS_OPTIONS}
                    value={params.daysInFuture}
                    onSelect={(d) => setParam("daysInFuture", d)}
                    unit="days_short"
                />
            </Box>

            {/* Compensate stockouts — measure velocity over days-with-stock only, so historical
                stockouts don't depress the rate. Materially changes advice for often-empty SKUs. */}
            <Box sx={{ display: "flex", alignItems: "flex-start",flexDirection:"column", }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, "& svg": { color: theme.palette.mode === "dark" ? colors.txt['secondary'] : colors.txt['softened'] } }}>
                    <Text color={theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light']}>
                        {t("include_missed_sales")}
                    </Text>
                    <EMGeneralExplainerBox
                        title={t("include_missed_sales")}
                        text={t("include_missed_sales_tooltip")}
                    />
                </Box>
                <ShSwitch
                    checked={!!params.includeMissedSales}
                    onChange={(e) => setParam("includeMissedSales", e.target.checked)}
                    sx={{
                        "& .MuiSwitch-switchBase, & .MuiSwitch-switchBase.Mui-checked": { color: "#fff" },
                        "& .MuiSwitch-track": { background: "rgba(255,255,255,.22)", opacity: 1 },
                        "& .Mui-checked + .MuiSwitch-track": { background: colors.blue[400], opacity: 1 },
                    }}
                />
            </Box>
            </>
            )}
        </Box>
    );
};

export default PurchaseAdviceFilters;
